import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigureTotpModal } from "./ConfigureTotpModal";
import { ConfigureTotpModalSuccess } from "./ConfigureTotpModalSuccess";
import { FavouriteMethodModal } from "./FavouriteMethodModal";
import {
  ManageAuthenticationButton,
  ProfileMfaModal,
  ProfileMfaModalContent,
  Separator
} from "./ProfileMfaConfig.styles";
import { MFA_FACTOR } from "../../consts";
import { useAuthStore } from "../../stores";
import { getEnvironmentVariables } from "../../utils/general";
import { getCorporateMfaFactors } from "../../utils/mfa";

const { loginStandardCognito } = getEnvironmentVariables();

type ModalState = "favourite-method" | "configure-totp" | "configure-totp-success" | null;

type ProfileMfaConfigProps = {
  showSeparator?: boolean;
};

export function ProfileMfaConfig({ showSeparator = true }: ProfileMfaConfigProps) {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ModalState>(null);
  const [isBusy, setIsBusy] = useState(false);
  const corporateMfaConfig = useAuthStore(state => state.corporateInfo?.corporate_mfa_config);
  const enabledFactors = useMemo(() => getCorporateMfaFactors(corporateMfaConfig), [corporateMfaConfig]);
  const isTotpEnabled = enabledFactors.includes(MFA_FACTOR.SOFTWARE_TOKEN_MFA);
  // Con il solo TOTP non c'è un preferito da scegliere: si gestisce direttamente il dispositivo.
  const isTotpOnly = isTotpEnabled && enabledFactors.length === 1;

  const closeModal = useCallback(() => {
    if (!isBusy) setModalState(null);
  }, [isBusy]);
  const openFavouriteMethodModal = useCallback(() => setModalState("favourite-method"), []);
  const openConfigureTotpModal = useCallback(() => setModalState("configure-totp"), []);
  const openConfigureTotpSuccessModal = useCallback(() => setModalState("configure-totp-success"), []);
  const onPreferenceSaved = () => {
    setIsBusy(false);
    setModalState(null);
  };

  if (!isTotpEnabled || !loginStandardCognito) return null;

  return (
    <>
      { showSeparator && <Separator /> }
      <ManageAuthenticationButton
        onClick={ isTotpOnly ? openConfigureTotpModal : openFavouriteMethodModal }
        type="button"
      >
        { t(isTotpOnly ? "profile_mfa_manage_device" : "profile_mfa_manage_authentication") }
      </ManageAuthenticationButton>

      <ProfileMfaModal onClose={ closeModal } open={ modalState !== null }>
        <ProfileMfaModalContent
          aria-busy={ isBusy }
          aria-label={ t("profile_mfa_manage_authentication") }
          aria-modal="true"
          role="dialog"
        >
          { modalState === "favourite-method" && <FavouriteMethodModal
            enabledFactors={ enabledFactors }
            onBusyChange={ setIsBusy }
            onClose={ closeModal }
            onSaved={ onPreferenceSaved }
            openConfigureTotpModal={ openConfigureTotpModal }
          /> }
          { modalState === "configure-totp" && <ConfigureTotpModal
            onBusyChange={ setIsBusy }
            onCancel={ isTotpOnly ? closeModal : openFavouriteMethodModal }
            openConfigureTotpSuccessModal={ openConfigureTotpSuccessModal }
          /> }
          { modalState === "configure-totp-success" && <ConfigureTotpModalSuccess onClose={ closeModal } /> }
        </ProfileMfaModalContent>
      </ProfileMfaModal>
    </>
  );
}
